import type React from 'react';

import type { Body, Meta, Uppy } from '@uppy/core';

import { Text } from '@change/ds-core-ui';
import { Form } from '@change/ds-forms';

import { Translate } from '@change-corgi/core/react/i18n';

import type { EndorsementFormData } from '../../shared/types';
import { endorsementFormSchema, type EndorsementFormSchema } from '../../shared/validation';
import { EndorsementModal } from '../EndorsementModal';
import { EndorsementPreview } from '../EndorsementPreview';

import { EndorsementFormFields } from './Fields';
import { EndorsementFormFooter } from './Footer';
import { EndorsementFormHeader } from './Header';

type EndorsementFormProps = {
	title: string;
	backButtonAriaLabel: string;
	defaultValues: EndorsementFormData;
	isSubmitting: boolean;
	isHelpModalOpen: boolean;
	isSubmitModalOpen: boolean;
	onBack: () => void;
	onSubmit: (data: EndorsementFormSchema) => void | Promise<void>;
	onInvalidSubmit?: () => void;
	onHelpClick: () => void;
	onCloseHelpModal: () => void;
	onCloseSubmitModal: () => void;
	onConfirmSubmit: () => void | Promise<void>;
	pageContainer: string;
	contentContainer: string;
	formSection: string;
	previewSection: string;
	testId?: string;
	uppy: Uppy<Meta, Body>;
	image: string | undefined;
	isTermsAgreed: boolean;
	onTermsChange: (checked: boolean) => void;
};

export const EndorsementForm: React.FC<EndorsementFormProps> = ({
	title,
	backButtonAriaLabel,
	defaultValues,
	isSubmitting,
	isHelpModalOpen,
	isSubmitModalOpen,
	onBack,
	onSubmit,
	onInvalidSubmit,
	onHelpClick,
	onCloseHelpModal,
	onCloseSubmitModal,
	onConfirmSubmit,
	pageContainer,
	contentContainer,
	formSection,
	previewSection,
	testId,
	uppy,
	image,
	isTermsAgreed,
	onTermsChange,
}) => {
	return (
		<Form
			schema={endorsementFormSchema}
			defaultValues={defaultValues}
			onSubmit={onSubmit}
			className={pageContainer}
			data-testid={testId}
			data-qa="endorsement-form"
		>
			{({ watch, handleSubmit, setValue }) => {
				const formData = watch();
				const statementCharCount = (formData.statement || '').length;

				const handleFormSubmit = async () => {
					// Sync photoUrl field with image state before validation
					setValue('photoUrl', image || '', { shouldValidate: false });

					await handleSubmit(onSubmit, () => {
						// Called when validation fails
						onInvalidSubmit?.();
					})();
				};

				return (
					<>
						<EndorsementFormHeader onBack={onBack} title={title} backButtonAriaLabel={backButtonAriaLabel} />

						<main className={contentContainer}>
							<div className={formSection}>
								<EndorsementFormFields statementCharCount={statementCharCount} uppy={uppy} image={image} />
							</div>

							<div className={previewSection}>
								<Text variant="body" fontWeight="bold" className="text-typography-strong mb-4">
									<Translate value="corgi.petition.starter_dashboard_v2.endorsements.add.preview_heading" />
								</Text>
								<EndorsementPreview
									name={formData.name}
									title={formData.title}
									statement={formData.statement}
									photoUrl={image}
									url={formData.url}
								/>
							</div>
						</main>

						<EndorsementFormFooter
							onClick={handleFormSubmit}
							onHelpClick={onHelpClick}
							data-qa="endorsement-form-footer"
						/>

						<EndorsementModal
							variant="help"
							isOpen={isHelpModalOpen}
							onClose={onCloseHelpModal}
							formData={{ ...formData, statement: formData.statement || '' }}
						/>

						<EndorsementModal
							variant="submit"
							isOpen={isSubmitModalOpen}
							isSubmitting={isSubmitting}
							onClose={onCloseSubmitModal}
							onConfirm={onConfirmSubmit}
							formData={{ ...formData, statement: formData.statement || '' }}
							isTermsAgreed={isTermsAgreed}
							onTermsChange={onTermsChange}
						/>
					</>
				);
			}}
		</Form>
	);
};
